/**
 * DEV-2684 Adım 5 — tek yetki kontrol noktası (helper + router guard ortak).
 *
 * Karar tek yerde verilir: yeni modda sunucunun /ben yanıtındaki `yetki` alanından
 * (birleşik anahtar haritası) okunur; eski modda (yetki yok ya da mod:"eski") bugünkü
 * `roles[0].permissions` yolu korunur. Böylece işaret kapalı büroda davranış değişmez.
 * (Backend: BenYetkiOzeti eski modda `anahtarlar`'ı null bırakır ve ekranın legacy yolu
 * kullanmaya devam etmesini bekler — UserController@ben notu.)
 *
 * Yönetici bypass'ı her iki modda korunur: yeni modda sunucunun hesapladığı
 * `yetki.yonetici`, eski modda herhangi bir rolün `is_admin`'i.
 *
 * Ayrı, (yalnız saf enum'a bağlı) modül: `helper.ts` test zincirinde (@/store -> uyap-plugin)
 * import edilemediği için karar mantığı burada birim test edilebilir tutulur. @/enum/YetkiModulleri
 * saf veri/enum taşır, ağır bir bağımlılık getirmez.
 */
import {YetkiKademe} from "@/enum/YetkiModulleri";

/** DEV-2684 — /ben yanıtındaki `yetki` alanının şekli (BenYetkiOzeti çıktısı). */
export interface BenYetki {
    surum: number;
    mod: 'eski' | 'yeni';
    yonetici?: boolean;
    hata?: boolean;
    moduller: Record<string, { kademe: YetkiKademe; ekle: boolean; degistir: boolean; sil: boolean }> | null;
    anahtarlar: string[] | null;
    /** Yalnız eski modda: yöneticiye "yeni sisteme geç" düğmesi açık mı (sunucu ölçer). */
    gecis_dugmesi?: boolean;
}

/**
 * DEV-2684 — yeni yetki modu tespiti tek yerde. Sunucunun /ben yanıtındaki `yetki.mod`
 * "yeni" ise yeni matris/rol-atama akışı; alan yoksa veya "eski" ise bugünkü davranış.
 * Hem `kullaniciYetkili` hem OfisTercihlerView bunu kullanır (kopya kontrol kalmasın).
 */
export function yeniYetkiModu(user: any): boolean {
    return !!(user && user.yetki && user.yetki.mod === 'yeni');
}

/**
 * DEV-2684 Model Y — kullanıcı Sistem Yöneticisi mi?
 *
 * Tek kaynak /ben yanıtındaki `yetki.yonetici`'dir; sunucu bunu HEM eski HEM yeni modda
 * hesaplar (BenYetkiOzeti). `yetki` alanı henüz gelmemişse (deploy öncesi açık oturum,
 * tazeleme öncesi) bugünkü `roles[].is_admin` yoluna düşülür. Hata yanıtında
 * (`yetki.hata === true`) güvenli varsayılan yöneticinin DEĞİL olmasıdır (fail-closed):
 * yetki belirsizken yetki yönetimi ekranları açılmaz.
 */
export function kullaniciYonetici(user: any): boolean {
    if (!user) {
        return false;
    }
    const yetki = user.yetki;
    if (yetki) {
        if (yetki.hata === true) {
            return false;
        }
        return yetki.yonetici === true;
    }
    // `yetki` alanı yok: eski davranış — herhangi bir rolü is_admin olan kullanıcı yönetici.
    return Array.isArray(user.roles) && user.roles.some((r: any) => r && r.is_admin == 1);
}

/**
 * DEV-2684 — `yalnizYonetici` kalem/rota kapısı (menü ve router ortak). Yönetici her zaman geçer.
 * `yeniModAnahtari` (eskiModAnahtari'nın yeni moddaki karşılığı) verilmişse YENİ modda o anahtar da
 * açar; eski modda ve `yetki` alanı gelmemiş oturumda yalnız yönetici (T5).
 */
export function yalnizYoneticiErisimi(user: any, yeniModAnahtari?: string): boolean {
    if (kullaniciYonetici(user)) {
        return true;
    }
    return !!yeniModAnahtari && yeniYetkiModu(user) && kullaniciYetkili(user, yeniModAnahtari);
}

/**
 * DEV-2684 Model Y — rota meta'sına göre erişim kararı.
 *
 * `yalnizYonetici: true` taşıyan rota yalnızYoneticiErisimi kapısından geçer (meta.yeniModAnahtari
 * ile). Bu bayrağı taşımayan rotalarda kapı her zaman true döner; o rotaların izinKey kontrolü
 * çağıran tarafta (router guard) ayrıca yapılır.
 */
export function rotaErisilebilir(meta: any, user: any): boolean {
    if (!meta || meta.yalnizYonetici !== true) {
        return true;
    }
    return yalnizYoneticiErisimi(user, meta.yeniModAnahtari);
}

/**
 * `eskiModAnahtari`: yalnız ESKİ modda (Spatie izni) bakılacak anahtar. Yeni modda `key` (modül
 * anahtarı) geçerlidir. Menü kalemi anahtarı yalnız yeni matriste var olan bir modülse (ör.
 * `hesaplamalar`) eski modda karşılığı olmadığından, eski modda rotanın bakacağı var olan izin
 * burada verilir; menü ve rota böylece aynı kullanıcıya açılır.
 */
export function kullaniciYetkili(user: any, key: string, eskiModAnahtari?: string): boolean {
    if (!user) {
        return false;
    }

    const yetki = user.yetki;
    if (yeniYetkiModu(user)) {
        // Hata yanıtında yetki belirsizdir: hiçbir şey açılmaz (kullaniciYonetici ile aynı fail-closed kural).
        if (yetki.hata === true) {
            return false;
        }
        // Yeni modda tek yetki kaynağı sunucunun ürettiği haritadır; yönetici de orada.
        if (yetki.yonetici === true) {
            return true;
        }
        return Array.isArray(yetki.anahtarlar) && yetki.anahtarlar.indexOf(key) !== -1;
    }

    // Eski mod veya yetki alanı henüz gelmemiş (deploy öncesi açık oturum, tazeleme öncesi).
    if (eskiModAnahtari) {
        key = eskiModAnahtari;
    }
    // Yönetici çok rollü olabilir (admin + meslek rolü): sıraya bakmadan herhangi bir rol
    // is_admin ise izin ver. Değilse bugünkü ilk rolün permissions yolu.
    if (Array.isArray(user.roles) && user.roles.some((r: any) => r && r.is_admin == 1)) {
        return true;
    }
    const rol = Array.isArray(user.roles) && user.roles.length > 0 ? user.roles[0] : null;
    if (!rol) {
        return false;
    }
    return Array.isArray(rol.permissions)
        && rol.permissions.some((permission: any) => permission && permission.name === key);
}

/** Master'da kapısız düğmeye YALNIZ yeni modda kapı; eski modda master gibi açık (sunucu eski modda rota kuralına bakmaz). */
export function kullaniciYetkiliYeniModda(user: any, key: string): boolean {
    return yeniYetkiModu(user) ? kullaniciYetkili(user, key) : true;
}

/** Tersi: master'daki izin YALNIZ eski modda sorulur; yeni modda bu kapı yok (e-İcraPro, K3: rol sorulmaz, karar lisansta). */
export function kullaniciYetkiliEskiModda(user: any, key: string): boolean {
    return yeniYetkiModu(user) ? true : kullaniciYetkili(user, key);
}
